'use client'

import React, { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react'
import * as yup from 'yup'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import SingleInputForm from '../Forms/SingleInputForm/SingleInputForm'
import { useSeller } from '@/app/_providers/SellerProvider/SellerProvider'
import TXT from '@/messages'
import { applyPromoCodeAction } from '@/app/_actions/cart.actions'
import { unwrapActionResult } from '@/utils/errors.utils'
import { MUTATION_KEYS, QUERY_KEYS } from '@/config/tanstack-querykeys.config'
import { useCart } from '@/app/_providers/CartProvider/CartProvider'

type Props = {
  onSuccess?: (cart: Transformed.Cart) => void
  onError?: () => void
}

const CouponInput: FunctionComponent<Props> = ({
  onSuccess,
  onError,
}: Props) => {
  const queryClient = useQueryClient()
  const { id: sellerId } = useSeller()
  const { cart } = useCart()
  const inputRef = useRef<HTMLInputElement>(null)
  const [hasSubmitted, setHasSubmitted] = useState(false)

  const [couponCode, setCouponCode] = useState('')
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [submitError, setSubmitError] = useState(false)

  const isValid = useMemo(() => {
    const { formErrors: { couponCode: couponCodeError } } = TXT
    const schema = yup.object()
      .shape({
        couponCode: yup.string()
          .matches(/^[A-Za-z0-9]+$/, couponCodeError)
          .min(4),
      })

    return schema.isValidSync({ couponCode })
  }, [couponCode])

  const applyPromoCodeMutation = useMutation({
    mutationKey: [MUTATION_KEYS.APPLY_PROMO_CODES, cart.id, sellerId],
    mutationFn: (codes: string[]) => applyPromoCodeAction(cart.id, codes, sellerId).then(unwrapActionResult),
    onSuccess: (cartResponse) => {
      setHasSubmitted(true)
      queryClient.setQueryData([QUERY_KEYS.CART, cartResponse.id], cartResponse)
      onSuccess?.(cartResponse)
      setCouponCode('')
      setSubmitSuccess(true)
    },
    onError: () => {
      setHasSubmitted(true)
      setSubmitError(true)
      setSubmitSuccess(false)
      if (onError) onError()
    },
  })

  useEffect(() => {
    if (applyPromoCodeMutation.error) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSubmitError(true)
    }
  }, [applyPromoCodeMutation.error])

  // This is shown conditionally, focus the input when shown
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
    }
    // prevents an error when unmounting
    return () => undefined
  }, [])

  return (
    <SingleInputForm
      ref={inputRef}
      name={TXT.couponCode.name}
      value={couponCode}
      label={TXT.couponCode.label}
      placeholder={TXT.couponCode.placeholder}
      prefix={TXT.couponCode.prefix}
      onChange={(event) => {
        setCouponCode(event.target.value)
        setHasSubmitted(false)
        setSubmitError(false)
      }}
      hasSubmitted={hasSubmitted}
      isLoading={applyPromoCodeMutation.isPending}
      isValid={isValid || submitError}
      submitError={submitError}
      submitSuccess={submitSuccess}
      errorMessage={TXT.couponCode.errorMessage}
      successMessage={TXT.couponCode.successMessage}
      onSubmit={(event) => {
        event.preventDefault()
        setHasSubmitted(true)
        setSubmitSuccess(false)
        if (couponCode.length <= 0) {
          return
        }
        applyPromoCodeMutation.mutate([couponCode.trim()])
      }}
    />
  )
}

export default CouponInput
